import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { Calendar, Clock, User, Mail, Phone, X, AlertCircle } from 'lucide-react';
import { format, parseISO } from 'date-fns';

interface Booking {
  id: string;
  attendeeName: string;
  attendeeEmail: string;
  attendeePhone?: string;
  startTime: string;
  endTime: string;
  timezone: string;
  status: 'CONFIRMED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
  attendeeNotes?: string;
  cancelledAt?: string;
  page: {
    title: string;
    slug: string;
  };
}

export default function BookingsDashboard() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await api.getBookings() as { bookings: Booking[] };
      setBookings(data.bookings);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!selectedBooking) return;

    try {
      await api.cancelBooking(selectedBooking.id, cancelReason);
      setBookings(bookings.map(b => 
        b.id === selectedBooking.id 
          ? { ...b, status: 'CANCELLED', cancelledAt: new Date().toISOString() }
          : b
      ));
      setShowCancelModal(false);
      setCancelReason('');
      setSelectedBooking(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800';
      case 'COMPLETED':
        return 'bg-blue-100 text-blue-800';
      case 'CANCELLED':
        return 'bg-red-100 text-red-800';
      case 'NO_SHOW':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const upcomingBookings = bookings.filter(b => 
    b.status === 'CONFIRMED' && new Date(b.startTime) > new Date()
  );

  const pastBookings = bookings.filter(b => 
    b.status === 'COMPLETED' || 
    b.status === 'CANCELLED' ||
    b.status === 'NO_SHOW' ||
    (b.status === 'CONFIRMED' && new Date(b.startTime) <= new Date())
  );

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading bookings...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Bookings</h1>
            <p className="mt-2 text-gray-600">Manage your appointments and meetings</p>
          </div>
          <div className="flex space-x-4">
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Upcoming</p>
              <p className="text-2xl font-bold text-blue-600">{upcomingBookings.length}</p>
            </div>
            <div className="bg-white px-4 py-2 rounded-lg shadow-sm">
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{bookings.length}</p>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Upcoming Bookings */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Upcoming Meetings</h2>
          
          {upcomingBookings.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No upcoming meetings</p>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                        <span className="text-sm text-gray-500">
                          {booking.page.title}
                        </span>
                      </div>
                      
                      <div className="flex items-center text-lg font-semibold text-gray-900 mb-2">
                        <User className="h-5 w-5 mr-2 text-gray-400" />
                        {booking.attendeeName}
                      </div>
                      
                      <div className="flex items-center text-gray-600 mb-1">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        {format(parseISO(booking.startTime), 'EEEE, MMMM d, yyyy')}
                      </div>
                      
                      <div className="flex items-center text-gray-600 mb-2">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        {format(parseISO(booking.startTime), 'h:mm a')} - {format(parseISO(booking.endTime), 'h:mm a')}
                        <span className="ml-2 text-sm text-gray-500">({booking.timezone})</span>
                      </div>
                      
                      <div className="flex items-center text-gray-600">
                        <Mail className="h-4 w-4 mr-2 text-gray-400" />
                        {booking.attendeeEmail}
                        {booking.attendeePhone && (
                          <>
                            <Phone className="h-4 w-4 ml-4 mr-2 text-gray-400" />
                            {booking.attendeePhone}
                          </>
                        )}
                      </div>
                      
                      {booking.attendeeNotes && (
                        <div className="mt-3 p-3 bg-gray-50 rounded text-sm text-gray-600">
                          <strong>Notes:</strong> {booking.attendeeNotes}
                        </div>
                      )}
                    </div>
                    
                    <button
                      onClick={() => {
                        setSelectedBooking(booking);
                        setShowCancelModal(true);
                      }}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded"
                      title="Cancel booking"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Past Bookings */}
        {pastBookings.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Past Meetings</h2>
            
            <div className="space-y-4">
              {pastBookings.slice(0, 10).map((booking) => (
                <div
                  key={booking.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 opacity-75"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                          {booking.status}
                        </span>
                        <span className="text-sm text-gray-500">
                          {format(parseISO(booking.startTime), 'MMM d, yyyy')}
                        </span>
                      </div>
                      
                      <p className="font-medium text-gray-900">{booking.attendeeName}</p>
                      <p className="text-sm text-gray-500">{booking.page.title}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Cancel Modal */}
        {showCancelModal && selectedBooking && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <AlertCircle className="h-6 w-6 text-red-600 mr-2" />
                <h3 className="text-lg font-semibold">Cancel Booking</h3>
              </div>
              
              <p className="text-gray-600 mb-4">
                Are you sure you want to cancel the meeting with {selectedBooking.attendeeName} on{' '}
                {format(parseISO(selectedBooking.startTime), 'MMMM d')}?
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for cancellation (optional)
                </label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  rows={3}
                  placeholder="Enter reason..."
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCancelModal(false);
                    setCancelReason('');
                    setSelectedBooking(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Keep Booking
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                  Cancel Meeting
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
}
